import { resolveCookieDomainAttribute } from './cookie-domain';

const TRACKING_CONSENT_COOKIE_NAME = 'ef_tracking_consent_v1';
const TRACKING_CONSENT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;
const TRACKING_CONSENT_CHANGE_EVENT = 'ef-tracking-consent-change';

export type TrackingConsentDecision = 'accepted' | 'declined';

type StoredTrackingConsent = {
  version: 1;
  marketing: boolean;
  updatedAt: string;
};

function getCookie(name: string) {
  if (typeof document === 'undefined') {
    return null;
  }

  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return part.slice(prefix.length);
    }
  }

  return null;
}

function resolveCookieDomain() {
  if (typeof window === 'undefined') {
    return '';
  }

  return resolveCookieDomainAttribute(window.location.hostname);
}

function writeCookieValue(name: string, value: string, maxAgeSeconds: number) {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domain = resolveCookieDomain();
  document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${domain}${secure}`;
}

export function readTrackingConsentDecision(): TrackingConsentDecision | null {
  const rawValue = getCookie(TRACKING_CONSENT_COOKIE_NAME);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(rawValue)) as StoredTrackingConsent;
    if (parsed?.marketing === true) {
      return 'accepted';
    }
    if (parsed?.marketing === false) {
      return 'declined';
    }
  } catch (_error) {
    return null;
  }

  return null;
}

export function writeTrackingConsentDecision(decision: TrackingConsentDecision) {
  const payload: StoredTrackingConsent = {
    version: 1,
    marketing: decision === 'accepted',
    updatedAt: new Date().toISOString(),
  };

  writeCookieValue(
    TRACKING_CONSENT_COOKIE_NAME,
    encodeURIComponent(JSON.stringify(payload)),
    TRACKING_CONSENT_COOKIE_MAX_AGE_SECONDS,
  );

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TRACKING_CONSENT_CHANGE_EVENT, { detail: decision }));
  }
}

export const trackingConsent = {
  cookieName: TRACKING_CONSENT_COOKIE_NAME,
  cookieMaxAgeSeconds: TRACKING_CONSENT_COOKIE_MAX_AGE_SECONDS,
  changeEventName: TRACKING_CONSENT_CHANGE_EVENT,
};
